import { html, nothing } from "lit";
import { icons } from "../icons.js";

export interface AuthLoginState {
  email: string;
  password: string;
  error: string | null;
  loading: boolean;
  mode: "login" | "register";
}

export const DEFAULT_AUTH_LOGIN_STATE: AuthLoginState = {
  email: "",
  password: "",
  error: null,
  loading: false,
  mode: "login",
};

export interface AuthLoginHandlers {
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onSubmit: () => void;
  onGoogleLogin: () => void;
  onToggleMode: () => void;
  onForgotPassword: () => void;
}

// Google icon SVG
const googleIcon = html`
  <svg viewBox="0 0 24 24" class="auth__google-icon">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
`;

export function renderAuthLogin(state: AuthLoginState, handlers: AuthLoginHandlers) {
  const { email, password, error, loading, mode } = state;
  const isLogin = mode === "login";

  return html`
    <div class="auth">
      <div class="auth__container">
        <div class="auth__brand">
          <div class="auth__brand-header">
            <div class="auth__logo">
              <img src="/tdai-logo-transparent.png" alt="TDAI" />
            </div>
            <div class="auth__brand-text">
              <h1 class="auth__title">
                ${isLogin ? "Welcome to TorqueDesk AI" : "Create your TorqueDesk AI account"}
              </h1>
            </div>
          </div>

          <div class="auth__brand-hero">
            <div class="auth__brand-george-wrap">
              <img src="/george-auth.png" alt="George" class="auth__brand-george" />
            </div>
            <p class="auth__brand-caption">George, your AI Commander</p>
            <div class="auth__brand-pills">
              <span class="auth__brand-pill auth__brand-pill--active">Agent CRM</span>
              <span class="auth__brand-pill">Accounting</span>
              <span class="auth__brand-pill">Banking</span>
              <span class="auth__brand-pill">Management</span>
            </div>
          </div>

          <p class="auth__tagline">
            Deploy an AI workforce that runs operations 24/7.<br/>
            Login once. Control every workflow from one workspace.
          </p>
        </div>

        <div class="auth__card">
          <!-- Google OAuth Button -->
          <button
            class="btn auth__google-btn"
            @click=${handlers.onGoogleLogin}
            ?disabled=${loading}
          >
            ${googleIcon}
            <span>Continue with Google</span>
          </button>

          <div class="auth__divider">
            <span>or</span>
          </div>

          <!-- Email/Password Form -->
          <form class="auth__form" @submit=${(e: Event) => {
            e.preventDefault();
            handlers.onSubmit();
          }}>
            <div class="auth__field">
              <label for="email" class="auth__label">Email</label>
              <input
                id="email"
                type="email"
                class="auth__input ${error ? "auth__input--error" : ""}"
                placeholder="you@example.com"
                .value=${email}
                @input=${(e: Event) => handlers.onEmailChange((e.target as HTMLInputElement).value)}
                ?disabled=${loading}
                autocomplete="email"
                required
              />
            </div>

            <div class="auth__field">
              <label for="password" class="auth__label">Password</label>
              <input
                id="password"
                type="password"
                class="auth__input ${error ? "auth__input--error" : ""}"
                placeholder="${isLogin ? "Enter your password" : "Create a password"}"
                .value=${password}
                @input=${(e: Event) => handlers.onPasswordChange((e.target as HTMLInputElement).value)}
                ?disabled=${loading}
                autocomplete=${isLogin ? "current-password" : "new-password"}
                minlength="8"
                required
              />
            </div>

            ${
              error
                ? html`
              <div class="auth__error">
                ${icons.alertCircle}
                <span>${error}</span>
              </div>
            `
                : nothing
            }

            ${
              isLogin
                ? html`
              <button
                type="button"
                class="auth__forgot-link"
                @click=${handlers.onForgotPassword}
              >
                Forgot password?
              </button>
            `
                : nothing
            }

            <button
              type="submit"
              class="btn primary auth__submit-btn"
              ?disabled=${!email.trim() || !password.trim() || loading}
            >
              ${
                loading
                  ? html`<span class="spinner"></span> ${isLogin ? "Signing in..." : "Creating account..."}`
                  : isLogin
                    ? "Sign In"
                    : "Create Account"
              }
            </button>
          </form>

          <div class="auth__footer">
            <span class="auth__footer-text">
              ${isLogin ? "Don't have an account?" : "Already have an account?"}
            </span>
            <button
              type="button"
              class="auth__toggle-link"
              @click=${handlers.onToggleMode}
            >
              ${isLogin ? "Sign up" : "Sign in"}
            </button>
          </div>
        </div>

        <p class="auth__terms">
          By continuing, you agree to our
          <a href="/terms" target="_blank">Terms of Service</a>
          and
          <a href="/privacy" target="_blank">Privacy Policy</a>
        </p>
      </div>
    </div>
  `;
}
